import { APIService } from "../Service/API.Service";

// ─── Response Types ─────────────────────────────────────────────────────────

export interface EvalCaseDefinition {
  id: string;
  title?: string;
  category: string;
  description: string;
  status: "live" | "building" | "backlog";
  /** The specific channel for this run (set by the harness when expanding multi-channel cases) */
  channel: string;
  /** All channels this case targets */
  channels: string[];
  locationFixture?: string;
  userFixture?: string;
  turns: { role: string; content?: string }[];
  criteria: { check: string; weight: 1 | 2 | 3; type?: string; toolName?: string }[];
  judgeContext?: string;
}

export interface EvalCriterionResult {
  criterion: { check: string; weight: 1 | 2 | 3; type?: string; toolName?: string };
  passed: boolean;
  reasoning: string;
  confidence: "high" | "medium" | "low";
}

export interface EvalTranscriptEntry {
  role: string;
  content: string;
  toolName?: string;
  toolInput?: Record<string, unknown>;
  timestamp: string;
}

export interface EvalPromptSection {
  id: string;
  text: string;
}

export interface EvalAttemptResultResponse {
  attempt: number;
  transcript: EvalTranscriptEntry[];
  criterionResults: EvalCriterionResult[];
  passed: boolean;
  score: number;
  botTokenUsage: { input: number; output: number };
  latencyMs: number;
  judgeTokenUsage: { input: number; output: number };
  error?: string;
}

export interface EvalCaseResultResponse {
  caseDefinition: EvalCaseDefinition;
  /** The specific channel this result was run on */
  channel: string;
  transcript: EvalTranscriptEntry[];
  criterionResults: EvalCriterionResult[];
  passed: boolean;
  score: number;
  botTokenUsage: { input: number; output: number };
  latencyMs: number;
  judgeTokenUsage: { input: number; output: number };
  systemPrompt?: EvalPromptSection[];
  error?: string;
  groundTruthValues?: { key: string; label: string; value?: string }[];
  appliedOverrides?: Record<string, string>;
  fixtureInfo?: {
    locationRole: string;
    locationName: string;
    userRole: string;
    userName: string;
    isSynthetic: boolean;
    injectedVars?: string[];
  };
  /** Tool calls invoked during this case, aggregated from the transcript. */
  toolsInvoked?: { name: string; callCount: number }[];
  /** Variable keys the bot read during this case (captured via AsyncLocalStorage). */
  variablesRead?: string[];
  /** Active prompt-section ids during this case. */
  promptSectionIds?: string[];
  previousResults?: {
    result: Omit<EvalCaseResultResponse, "previousResults">;
    replacedAt: string;
  }[];

  // ─── Within-run attempts (run-time stochasticity flushing) ────────────────
  /** All attempts for this (case × channel) within this run, oldest → newest. */
  attempts?: EvalAttemptResultResponse[];
  /** Total attempts that ran. */
  attemptCount?: number;
  /**
   * How many attempts passed. With `attemptCount` this is the per-case
   * "confidence signal" — rendered as `2/3 (67%)` in the UI. Pass rule
   * (top-level `passed`) is strict majority: `passCount * 2 > attemptCount`.
   */
  passCount?: number;
  /** Which attempt produced the top-level result fields (1-indexed). 0 if not a passing case. */
  passedOnAttempt?: number;
}

export interface BotEvalRecommendation {
  overall: string[];
  byCategory: Record<string, string[]>;
  byCase: Record<string, string>;
}

/** Operator note attached to a single (caseId × channel) within an eval run. */
export interface BotEvalCaseNote {
  text: string;
  /** ISO 8601 timestamp of the most recent edit. */
  updatedAt: string;
  /** Operator email (when authenticated identity is available). */
  updatedBy?: string;
}

/** A (caseId, channel) pair the run was started to execute. */
export interface EvalManifestEntry {
  caseId: string;
  channel: string;
}

export interface EvalRunStatusResponse {
  runId: number;
  status: string;
  totalCases: number;
  completedCases: number;
  passed: number;
  failed: number;
  expectedFailures: number;
  /** Strict-pass cohort: cases where ALL attempts passed. Equals `passed` for runs with attemptsPerCase=1. */
  reliablyPassed?: number;
  sourceOrgId: number;
  sourceOrgName: string;
  caseResults: EvalCaseResultResponse[];
  /**
   * The run's manifest — every (caseId, channel) pair this run was started
   * to execute. Set ONCE at run-start. Studio reads this so the case-list
   * view shows queued cases from second one. NULL for legacy runs that
   * predate the column.
   */
  manifest?: EvalManifestEntry[] | null;
  /** How many attempts each (case × channel) executed. 1 = legacy single-pass. */
  attemptsPerCase?: number;
  /** Case-group concurrency the run used. 1 = sequential / latency-accurate. */
  concurrency?: number;
  /** When the run reached terminal state. NULL while running. ISO 8601. */
  completedAt?: string | null;
  error?: string;
  createdAt: string;
  recommendations?: BotEvalRecommendation | null;
  recommendationCostCents?: number | null;
  recommendationTokens?: { input: number; output: number } | null;
  /** Operator notes per (caseId × channel) keyed `${caseId}:${channel}`. */
  notes?: Record<string, BotEvalCaseNote>;
  /** Bot commit hash captured at run-start. Surfaces as a link in the studio. */
  commitHash?: string;
  /** Eval suite ID — needed to faithfully reproduce the run via Duplicate. */
  suite?: string;
}

export interface EvalRunSummary {
  id: number;
  sourceOrgId: number;
  sourceOrgName: string;
  status: string;
  totalCases: number;
  completedCases: number;
  passed: number;
  failed: number;
  expectedFailures: number;
  /** Strict-pass cohort: cases where ALL attempts passed. */
  reliablyPassed?: number;
  /** How many attempts each (case × channel) executed. 1 = legacy single-pass. */
  attemptsPerCase?: number;
  /** Case-group concurrency the run used. 1 = sequential / latency-accurate. */
  concurrency?: number;
  /** When the run reached terminal state. NULL while running. ISO 8601. */
  completedAt?: string | null;
  categoryFilter?: string;
  statusFilter?: string;
  suite?: string;
  createdAt: string;
}

export interface EvalFixturePreview {
  locations: {
    fixtureRole: string;
    sourceId: number;
    name: string;
    address: string;
    featureCount: number;
    isSynthetic: boolean;
    matchScore: number;
    missingVars: string[];
    injectedVars: string[];
  }[];
  users: {
    fixtureRole: string;
    sourceId: number | null;
    name: string;
    phone: string;
    isSynthetic: boolean;
    matchReason: string;
  }[];
}

/** Mirrors PermutationSuiteResult (libs/policy-definitions) at the wire boundary. */
export interface PermutationSuiteResultDTO {
  passed: boolean;
  definitionCount: number;
  totalCombinations: number;
  validationErrors: { policyKey: string; message: string }[];
  results: {
    policyKey: string;
    title: string;
    combinationCount: number;
    probeCount: number;
    sweepCaseCount: number;
    problems: { answers: Record<string, string>; problem: string; text?: string }[];
  }[];
}

// ─── API ────────────────────────────────────────────────────────────────────

export class BotEvalAPI {
  static async listSuites(): Promise<{ id: string; name: string; description: string }[]> {
    return APIService.Bot.get<{ id: string; name: string; description: string }[]>("/evals/suites");
  }

  static async getCases(suite?: string): Promise<{ cases: EvalCaseDefinition[] }> {
    const params = suite ? { suite } : undefined;
    return APIService.Bot.get<{ cases: EvalCaseDefinition[] }>("/evals/cases", params);
  }

  static async previewFixtures(sourceOrgId: number): Promise<EvalFixturePreview> {
    return APIService.Bot.get<EvalFixturePreview>(`/evals/preview/${sourceOrgId}`);
  }

  static async runPolicyPermutations(): Promise<PermutationSuiteResultDTO> {
    return APIService.Bot.post<PermutationSuiteResultDTO>("/evals/policy-permutations", {});
  }

  static async startPermutationSweep(body: {
    sourceOrgId: number;
    policyKeys?: string[];
    channel?: string;
    attemptsPerCase?: number;
    confirmLarge?: boolean;
    /** Per-worksheet combination sampling cap (max combinations tested per policy). Defaults to 150. */
    combinationCeiling?: number;
  }): Promise<{ runId: number }> {
    return APIService.Bot.post<{ runId: number }>("/evals/permutation-sweep", body);
  }

  static async listRuns(sourceOrgId?: number, limit?: number, suite?: string): Promise<EvalRunSummary[]> {
    const params: Record<string, unknown> = {};
    if (sourceOrgId) params.sourceOrgId = sourceOrgId;
    if (limit) params.limit = limit;
    if (suite) params.suite = suite;
    return APIService.Bot.get<EvalRunSummary[]>("/evals/runs", Object.keys(params).length > 0 ? params : undefined);
  }

  static async startRun(body: {
    sourceOrgId: number;
    suite?: string;
    category?: string;
    status?: string;
    activeOverrides?: { promptOverrides?: Record<string, number> };
    /** If provided, only run these specific case:channel combinations */
    selectedCases?: { caseId: string; channel: string }[];
    /**
     * How many times each (case × channel) executes within this run. The case
     * passes if any attempt passes. Defaults to 1 (legacy single-pass).
     * Server clamps to [1, 10].
     */
    attemptsPerCase?: number;
    /**
     * How many case-groups (story × all channels) run concurrently. Defaults
     * to 1 = sequential (legacy + latency-accurate). Bump to 4-8 for fast
     * iteration. Action cases auto-sequentialize. Server clamps to [1, 8].
     */
    concurrency?: number;
  }): Promise<{ runId: number }> {
    return APIService.Bot.post<{ runId: number }>("/evals/run", body);
  }

  static async getRunStatus(runId: number): Promise<EvalRunStatusResponse> {
    return APIService.Bot.get<EvalRunStatusResponse>(`/evals/run/${runId}`);
  }

  static async runSingleCase(
    caseId: string,
    sourceOrgId: number,
    activeOverrides?: { promptOverrides?: Record<string, number> },
    runId?: number
  ): Promise<EvalCaseResultResponse> {
    return APIService.Bot.post<EvalCaseResultResponse>("/evals/run-single", { caseId, sourceOrgId, activeOverrides, runId });
  }

  static async generateRecommendations(runId: number): Promise<BotEvalRecommendation> {
    return APIService.Bot.post<BotEvalRecommendation>(`/evals/run/${runId}/recommendations`, {});
  }

  /**
   * Save (or clear) an operator note for a (case × channel) in a specific run.
   * Empty `text` clears the slot. Server-side write is wrapped in a
   * row-locked transaction so it does not race the bot's progress flushes.
   */
  static async saveCaseNote(runId: number, body: { caseId: string; channel?: string; text: string }): Promise<{ note: BotEvalCaseNote | null }> {
    return APIService.Bot.post<{ note: BotEvalCaseNote | null }>(`/evals/run/${runId}/notes`, body);
  }
}
